import React, { useState, useEffect } from 'react';
import { usuariosAPI } from '../api/client';
import { Shield, Plus, Edit2, Trash2, X, UserCog, Lock, Eye, EyeOff } from 'lucide-react';

const PermisosPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [permisos, setPermisos] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre_completo: '',
    username: '',
    password: '',
    email: '',
    activo: 1,
    permisos: []
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      // Cargar usuarios y permisos disponibles
      const [usuariosRes, permisosRes] = await Promise.all([
        usuariosAPI.getAll(),
        usuariosAPI.getPermisos('all')
      ]);
      
      if (usuariosRes.data.success) {
        setUsuarios(usuariosRes.data.data);
      }
      
      if (permisosRes.data.success) {
        setPermisos(permisosRes.data.data);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar datos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (usuario = null) => {
    if (usuario) {
      setEditando(usuario);
      setFormData({
        id: usuario.id,
        nombre_completo: usuario.nombre_completo,
        username: usuario.username,
        password: '',
        email: usuario.email || '',
        activo: usuario.activo,
        permisos: usuario.permisos || []
      });
    } else {
      setEditando(null);
      setFormData({
        nombre_completo: '',
        username: '',
        password: '',
        email: '',
        activo: 1,
        permisos: []
      });
    }
    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEditando(null);
  };

  const togglePermiso = (permisoId) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.includes(permisoId)
        ? prev.permisos.filter(id => id !== permisoId)
        : [...prev.permisos, permisoId]
    }));
  };

  const toggleModulo = (modulo) => {
    const permisosModulo = permisos[modulo] || [];
    const idsModulo = permisosModulo.map(p => p.id);
    const todosSeleccionados = idsModulo.every(id => formData.permisos.includes(id));
    
    if (todosSeleccionados) {
      setFormData(prev => ({
        ...prev,
        permisos: prev.permisos.filter(id => !idsModulo.includes(id))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permisos: [...new Set([...prev.permisos, ...idsModulo])]
      }));
    }
  };

  const aplicarPlantilla = (tipo) => {
    const clavesPlantillas = {
      admin: [], // Todos los permisos - se llenará dinámicamente
      vendedor: ['ventas.', 'productos.ver', 'clientes.', 'cotizaciones.'],
      cajero: ['ventas.', 'caja.'],
      bodeguero: ['productos.', 'inventario.'],
    };

    // Si es admin, seleccionar todos
    if (tipo === 'admin') {
      const todosLosIds = [];
      Object.values(permisos).forEach(modulo => {
        modulo.forEach(p => todosLosIds.push(p.id));
      });
      setFormData(prev => ({
        ...prev,
        permisos: todosLosIds
      }));
      return;
    }

    // Para otras plantillas, filtrar por claves
    const clavesBuscar = clavesPlantillas[tipo] || [];
    const permisosSeleccionados = [];
    
    Object.values(permisos).forEach(modulo => {
      modulo.forEach(permiso => {
        // Verificar si la clave del permiso coincide con alguna de las claves de la plantilla
        if (clavesBuscar.some(clave => permiso.clave.startsWith(clave))) {
          permisosSeleccionados.push(permiso.id);
        }
      });
    });

    setFormData(prev => ({
      ...prev,
      permisos: permisosSeleccionados
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (editando) {
        await usuariosAPI.update(formData);
        alert('Usuario actualizado exitosamente');
      } else {
        await usuariosAPI.create(formData);
        alert('Usuario creado exitosamente');
      }
      
      cerrarModal();
      cargarDatos();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await usuariosAPI.delete(id);
      alert('Usuario eliminado');
      cargarDatos();
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.message || error.message));
    }
  };

  const getModuloIcon = (modulo) => {
    const iconos = {
      ventas: '💰',
      productos: '📦',
      clientes: '👥',
      caja: '💵',
      inventario: '📊',
      reportes: '📈',
      configuracion: '⚙️',
      cotizaciones: '📋'
    };
    return iconos[modulo] || '📄';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCog className="w-8 h-8 text-primary-600" />
            Gestión de Usuarios y Permisos
          </h1>
          <p className="text-gray-600 mt-1">Administra usuarios y sus permisos de acceso</p>
        </div>
        <button
          onClick={() => abrirModal()}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nuevo Usuario
        </button>
      </div>

      {/* Lista de Usuarios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {usuarios.map((usuario) => (
          <div 
            key={usuario.id} 
            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <UserCog className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{usuario.nombre_completo}</h3>
                  <p className="text-sm text-gray-600">@{usuario.username}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => abrirModal(usuario)}
                  className="text-primary-600 hover:text-primary-900 p-1"
                  title="Editar"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {usuario.id !== 1 && (
                  <button
                    onClick={() => eliminarUsuario(usuario.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-500 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-1">
                <Lock className="w-4 h-4" />
                <span>{usuario.permisos?.length || 0} permisos</span>
              </div>
              <div className="flex items-center gap-1">
                {usuario.activo ? (
                  <>
                    <Eye className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">Activo</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 text-red-600" />
                    <span className="text-red-600">Inactivo</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Crear/Editar Usuario */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <UserCog className="w-6 h-6 text-primary-600" />
                  {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>
                <button onClick={cerrarModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Información Básica */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Usuario <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña {editando && '(dejar en blanco para no cambiar)'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="input-field"
                    required={!editando}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-field"
                  />
                </div>
              </div>

              {/* Plantillas Rápidas */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm">🎯 Plantillas Rápidas</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => aplicarPlantilla('admin')}
                    className="p-3 bg-white rounded-lg border-2 border-red-300 hover:border-red-500 hover:bg-red-50 transition-all text-center shadow-sm"
                  >
                    <div className="text-2xl mb-1">👑</div>
                    <div className="text-xs font-bold text-red-700">Admin</div>
                    <div className="text-xs text-gray-600 mt-1">Todos</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => aplicarPlantilla('vendedor')}
                    className="p-3 bg-white rounded-lg border-2 border-green-300 hover:border-green-500 hover:bg-green-50 transition-all text-center shadow-sm"
                  >
                    <div className="text-2xl mb-1">👨‍💼</div>
                    <div className="text-xs font-bold text-green-700">Vendedor</div>
                    <div className="text-xs text-gray-600 mt-1">Ventas</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => aplicarPlantilla('cajero')}
                    className="p-3 bg-white rounded-lg border-2 border-yellow-300 hover:border-yellow-500 hover:bg-yellow-50 transition-all text-center shadow-sm"
                  >
                    <div className="text-2xl mb-1">💵</div>
                    <div className="text-xs font-bold text-yellow-700">Cajero</div>
                    <div className="text-xs text-gray-600 mt-1">Caja</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => aplicarPlantilla('bodeguero')}
                    className="p-3 bg-white rounded-lg border-2 border-orange-300 hover:border-orange-500 hover:bg-orange-50 transition-all text-center shadow-sm"
                  >
                    <div className="text-2xl mb-1">📦</div>
                    <div className="text-xs font-bold text-orange-700">Bodeguero</div>
                    <div className="text-xs text-gray-600 mt-1">Inventario</div>
                  </button>
                </div>
              </div>

              {/* Selección Rápida por Módulos */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900 text-sm">✨ Selección por Módulo</h4>
                  <span className="text-xs text-gray-600">
                    <span className="font-bold text-primary-600">{formData.permisos.length}</span> permisos seleccionados
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.keys(permisos).map((modulo) => {
                    const permisosModulo = permisos[modulo] || [];
                    const idsModulo = permisosModulo.map(p => p.id);
                    const todosSeleccionados = idsModulo.every(id => formData.permisos.includes(id));
                    
                    return (
                      <button
                        key={modulo}
                        type="button"
                        onClick={() => toggleModulo(modulo)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          todosSeleccionados
                            ? 'bg-primary-600 border-primary-600 text-white shadow-md'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-primary-50'
                        }`}
                      >
                        <div className="text-2xl mb-1">{getModuloIcon(modulo)}</div>
                        <div className="text-xs font-semibold capitalize">{modulo}</div>
                        <div className="text-xs opacity-75 mt-1">
                          {idsModulo.filter(id => formData.permisos.includes(id)).length}/{idsModulo.length}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Permisos Detallados */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Permisos Individuales</h3>
                <div className="grid grid-cols-1 gap-4">
                  {Object.keys(permisos).map((modulo) => {
                    const permisosModulo = permisos[modulo] || [];
                    
                    return (
                      <div key={modulo} className="border-2 border-gray-200 rounded-lg p-4">
                        <h4 className="font-bold text-gray-900 capitalize flex items-center gap-2 mb-3">
                          <span className="text-2xl">{getModuloIcon(modulo)}</span>
                          {modulo}
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {permisosModulo.map((permiso) => {
                            const isSelected = formData.permisos.includes(permiso.id);
                            return (
                              <button
                                key={permiso.id}
                                type="button"
                                onClick={() => togglePermiso(permiso.id)}
                                className={`text-left p-3 rounded-lg border-2 transition-all ${
                                  isSelected
                                    ? 'bg-primary-50 border-primary-500 shadow-sm'
                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? 'bg-primary-600 border-primary-600'
                                      : 'bg-white border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                  <p className={`text-sm font-semibold ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                    {permiso.nombre}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="flex-1 btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : (editando ? 'Actualizar Usuario' : 'Crear Usuario')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermisosPage;
